import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/conversion-rate-optimierung";
const TITLE = "Conversion Rate Optimierung: Definition, Ablauf, Rechnung";
const DESCRIPTION =
  "Conversion Rate Optimierung erklärt: Definition, Formel, Ablauf eines CRO-Projekts, realistische Ergebnisse und wann sich der Aufwand im Onlineshop rechnet.";

const faqs = [
  {
    q: "Was ist Conversion Rate Optimierung?",
    a: "Conversion Rate Optimierung (CRO) ist die systematische Arbeit daran, dass ein größerer Anteil der vorhandenen Besucher eine gewünschte Handlung ausführt, im Shop also kauft. Grundlage sind Daten, gezielte Änderungen und Tests statt Geschmacksentscheidungen.",
  },
  {
    q: "Wie berechnet man die Conversion Rate?",
    a: "Conversion Rate = Bestellungen geteilt durch Sessions mal 100. Bei 25.000 Sessions und 350 Bestellungen sind das 1,4 Prozent.",
  },
  {
    q: "Was ist eine gute Conversion Rate im E-Commerce?",
    a: "Im DACH-Raum liegen die meisten Shops zwischen 1,2 und 1,8 Prozent. Über 3 Prozent erreichen vor allem Marken mit hohem Wiederkaufsanteil oder sehr engem Sortiment.",
  },
  {
    q: "Wie lange dauert ein CRO-Projekt?",
    a: "Ein fokussierter Sprint dauert vier Wochen: Woche 1 Analyse, Woche 2 und 3 Umsetzung, Woche 4 Tests und Auswertung. Belastbare Zahlen liegen je nach Traffic nach vier bis acht Wochen vor.",
  },
  {
    q: "Was bringt CRO im Vergleich zu mehr Werbebudget?",
    a: "Mehr Budget kostet jeden Monat erneut, eine höhere Conversion Rate wirkt auf jeden zukünftigen Besucher, auch auf organischen Traffic. Ob CRO oder Traffic der bessere Hebel ist, hängt von Ihren Zahlen ab und lässt sich vorab prüfen.",
  },
];

export const Route = createFileRoute("/ratgeber/conversion-rate-optimierung")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Conversion Rate Optimierung",
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Grundlagen"
      title="Conversion Rate Optimierung: Definition, Ablauf und Rechnung"
      intro="CRO ist keine Sammlung von Tricks, sondern eine Abfolge von Entscheidungen: messen, priorisieren, ändern, prüfen. Dieser Artikel erklärt die Begriffe, die Formeln und den Ablauf, so, wie er in echten Shops funktioniert."
      source="ratgeber-cro"
      sections={[
        {
          h: "Definition und Formel",
          p: [
            "Die Conversion Rate ist der Anteil der Sessions, aus denen eine Bestellung wird: Bestellungen geteilt durch Sessions mal 100. Optimierung bedeutet, diesen Anteil zu erhöhen, ohne mehr Traffic einzukaufen.",
            "Aussagekräftiger als die Conversion Rate allein ist der Umsatz pro Session. Er verbindet Kaufabschlussrate und Warenkorbhöhe und verhindert Fehlschlüsse, wenn ein Test zwar mehr Käufe, aber kleinere Warenkörbe bringt.",
          ],
        },
        {
          h: "Wo Umsatz typischerweise verloren geht",
          p: [
            "In den meisten Shops verteilt sich der Verlust auf immer dieselben Stellen. Die Reihenfolge unten entspricht der Häufigkeit, in der ich sie finde.",
          ],
          list: [
            "Mobile Produktseite: Preis, Versand und Verfügbarkeit nicht im ersten Bildschirm",
            "Checkout: zu viele Felder, überraschende Versandkosten, fehlende Zahlungsarten",
            "Ladezeit über drei Sekunden, vor allem bei bezahltem Traffic",
            "Fehlende Antworten auf Zweifel: Rückgabe, Lieferzeit, Größen, Material",
            "Unklare Angebotskommunikation auf Kategorie- und Startseite",
          ],
        },
        {
          h: "Der Ablauf eines Projekts",
          p: [
            "Zuerst wird gemessen: Trichter nach Gerät, Absprünge im Checkout, Suchanfragen im Shop, Sitzungsaufzeichnungen. Daraus entstehen Hypothesen mit erwartetem Effekt und Aufwand.",
            "Danach wird umgesetzt, im bestehenden Theme, versioniert, mit Rollback-Möglichkeit. Getestet wird nur dort, wo genug Traffic für Signifikanz vorhanden ist; darunter entscheidet Erfahrung plus Vorher-Nachher-Vergleich.",
          ],
        },
        {
          h: "Was realistisch herauskommt",
          p: [
            "Seriös lässt sich das vorab nicht beziffern. Der Ausgangszustand entscheidet: Ein Shop mit vielen offenen Schwachstellen hat mehr Luft als ein bereits gründlich optimierter.",
            "Rechenbeispiel: 25.000 Sessions, 70 € Warenkorb, 1,4 auf 2,0 Prozent ergibt rund 10.500 € Mehrumsatz pro Monat aus demselben Traffic.",
          ],
        },
        {
          h: "Wann sich CRO lohnt, und wann nicht",
          p: [
            "Das hängt von Traffic, Conversion-Rate, Warenkorb, Marge und Aufwand ab. Bei sehr wenig Traffic bringen Angebot, Sortiment und Traffic-Qualität meist mehr.",
            "Ebenfalls ungeeignet: Shops, deren Grundproblem im Produkt oder im Preis liegt. Optimierung verstärkt ein funktionierendes Angebot, sie ersetzt es nicht.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-conversion-rate-benchmark", label: "Benchmark ansehen" },
        { to: "/roi-rechner", label: "Effekt berechnen" },
        { to: "/methode", label: "Wie ich arbeite" },
      ]}
    />
  );
}
